import { useState, useEffect } from "react";
import useApi from "../customHooks/useAPI";
import useProfile from "../customHooks/useProfile";

const PrescriptionForm = ({ doctorId, patientId, onClose, firstName, lastName }) => {
    const initialState = {
        complaints: [""],
        history_of_illness: [""],
        notes: [""],
        allergy: [""],
        diagnosis: [""],
        advice: [""],
    };

    const [formData, setFormData] = useState(initialState);
    const [saving, setSaving] = useState(false);
    const {callApi} = useApi();

    const handleChange = (field, index, value) => {
        const updatedArray = [...formData[field]];
        updatedArray[index] = value;
        setFormData({ ...formData, [field]: updatedArray });
    };

    const handleAddField = (field) => {
        setFormData({ ...formData, [field]: [...formData[field], ""] });
    };

    const handleRemoveField = (field, index) => {
        const updatedArray = [...formData[field]];
        updatedArray.splice(index, 1);
        // Always ensure at least one empty field remains
        setFormData({ ...formData, [field]: updatedArray.length ? updatedArray : [""] });
    };

    const [profile, setProfile] = useState(null);
    const {checkProfileStatus} = useProfile();

    // Fetch doctor profile only once when the component loads
    useEffect(() => {
        const fetchProfile = async () => {
            const data = await checkProfileStatus(true);
            setProfile(data);
        };
        fetchProfile();
    }, []);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                "Doctor-Id" : doctorId,
                "Patient-Id":  patientId
            }

            const raw = JSON.stringify(formData);

            const result = await callApi({
                url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/uploadPrescription`,
                method: "POST",
                headers: myHeaders,
                body: raw,
            });

            console.log(result);
            alert("Prescription submitted successfully!");
            setFormData(initialState);
            onClose();
        } catch (err) {
            console.error(err);
            alert("Failed to submit prescription.");
        } finally {
            setSaving(false);
        }
    };

    const renderField = (field, label) => (
        <div className="mb-6 font-sans">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-1 mb-2">
                {label}
            </h3>
            {formData[field].map((item, index) => (
                <div key={index} className="flex items-start gap-2 mb-2">
                    <textarea
                        value={item}
                        onChange={(e) => handleChange(field, index, e.target.value)}
                        rows={2}
                        placeholder={`Enter ${label.toLowerCase()}...`}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y"
                    />

                    {/* Add Button (only on last item) */}
                    {index === formData[field].length - 1 && (
                        <button
                            type="button"
                            onClick={() => handleAddField(field)}
                            className="h-8 w-8 flex items-center justify-center bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                            title="Add another"
                        >
                            +
                        </button>
                    )}

                    {/* Remove Button (on all but first field) */}
                    {formData[field].length > 1 && (
                        <button
                            type="button"
                            onClick={() => handleRemoveField(field, index)}
                            className="h-8 w-8 flex items-center justify-center bg-red-500 text-white rounded-md hover:bg-red-600 transition"
                            title="Remove"
                        >
                            −
                        </button>
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-screen overflow-scroll">
            <form
                onSubmit={handleSubmit}
                className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-8 border border-gray-200"
            >
                {/* HEADER */}
                <div className="text-center bg-[#71B1CE] text-[#152B37] py-3 rounded-md mb-6">
                    <h1 className="text-xl font-bold tracking-wide">
                        CONSULTATION SUMMARY
                    </h1>
                </div>

                {/* TOP INFO SECTION */}
                <div className="flex flex-wrap justify-between text-sm text-gray-700 mb-8">
                    <div className="w-full sm:w-[48%] space-y-1">
                        <p><b>Consultation Date:</b> {new Date().toLocaleString()}</p>
                        <p><b>Consultant:</b> Dr. {profile?.first_name} {profile?.last_name}</p>
                        {/* <p><b>Consultation Type:</b> VC, First Visit</p> */}
                    </div>
                    <div className="w-full sm:w-[48%] space-y-1">
                        <p><b>Patient ID:</b> {patientId}</p>
                        <p><b>Patient Name:</b> {firstName} {lastName}</p>
                        {/* <p><b>Gender/Age/DOB:</b> Male, 23 Years, 13/12/2001</p>
                        <p><b>Patient Phone:</b> -</p>
                        <p><b>Patient Address:</b> Delhi, India</p> */}
                    </div>
                </div>

                {/* SECTIONS (same as PDF) */}
                {renderField("complaints", "Chief Complaints")}
                {renderField("history_of_illness", "History of Illness")}
                {renderField("notes", "Notes")}
                {renderField("allergy", "Allergy")}
                {renderField("diagnosis", "Diagnosis")}
                {renderField("advice", "Advice")}

                {/* FOOTER ACTIONS */}
                <div className="flex justify-end mt-8 gap-3 border-t pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400 transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-5 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition disabled:opacity-50"
                    >
                        {saving ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PrescriptionForm;
